import { useAppProvider } from "@/Provider/AppProvider";
import { ConfigurationsStateOutputItem } from "@/Schemas/Configurations/ConfigurationsStateOutput";
import { BaseStatisticsOutputItem } from "@/Schemas/Statistics/BaseStatisticsOutputItem";
import { Box, Chip, Icon, Tooltip, Typography, useTheme } from "@mui/material";
import { FaSortAmountDownAlt, FaSortAmountUpAlt } from "react-icons/fa";
import { TextOverflow, formatNumber, formatPercent, useLocalStorage } from "vig-ui-library";

export type StatesCardsProps = {
    items: BaseStatisticsOutputItem[];
};

export const StatesCards = ({ items }: StatesCardsProps) => {
    const [sort, setSort] = useLocalStorage("desc", "state-sort");

    const theme = useTheme();

    const { configurations } = useAppProvider();

    const states: StatesCardProps[] = configurations.state.commonItems
        .map((item: ConfigurationsStateOutputItem) => {
            const data = items.find((i) => i.id === item.id);

            const percentage = data?.percentage || 0;

            return {
                label: item.label,
                icon: <Icon sx={{ color: item.color, fontSize: 24 }}>{item.icon}</Icon>,
                count: data?.value || 0,
                percentage: percentage > 10 ? Math.round(percentage) : percentage,
            };
        })
        .sort((a, b) => {
            if (sort === "asc") {
                return a.count - b.count;
            }
            return b.count - a.count;
        });

    return (
        <Box
            sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                padding: 2,
                flexShrink: 0,
                borderRadius: `${theme.shape.borderRadius * 2}px`,
                backgroundColor: theme.palette.grey[100],
                gap: 1.5
            }}
        >
            <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}
                onClick={() => setSort(sort === "asc" ? "desc" : "asc")}
            >
                {sort === "asc" && <FaSortAmountUpAlt color={theme.palette.grey[600]} />}
                {sort === "desc" && <FaSortAmountDownAlt color={theme.palette.grey[600]} />}
                <Typography variant="body2" color={theme.palette.text.primary}>
                    {sort === "asc" ? "Najnižší počet" : "Najvyšší počet"}
                </Typography>
            </Box>

            {states.map((state, index) => (
                <StatesCard key={index} {...state} />
            ))}
        </Box>
    );
}

export type StatesCardProps = {
    icon: React.ReactNode;
    label: string;
    count: number;
    percentage: number;
}

export const StatesCard = (props: StatesCardProps) => {
    const theme = useTheme();

    return (
        <Box sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: 2,
            borderRadius: `${theme.shape.borderRadius}px`,
            backgroundColor: theme.palette.common.white,
            gap: 1.5,
            width: "100%",
        }}>
            <Box sx={{
                display: "flex",
                gap: 1,
                flexGrow: 1,
                width: "100%",
                overflow: "hidden",
            }}>
                <Box flexShrink={0} width={24} display="flex" alignItems="center">
                    {props.icon}
                </Box>

                <Tooltip title={props.label}>
                    <Box sx={{ flexGrow: 1, width: "100%", display: "flex", alignItems: "center", overflow: "hidden" }}>
                        <TextOverflow maxWidth="100%" variant="body2" color={theme.palette.text.secondary} text={props.label} lineHeight={1} />
                    </Box>
                </Tooltip>
            </Box>

            <Box sx={{
                display: "flex",
                gap: 1,
                flexShrink: 0,
                overflow: "hidden",
            }}>
                <Typography variant="body1" fontWeight={500} color={theme.palette.text.primary}>{formatNumber(props.count)}x</Typography>

                <Chip
                    label={formatPercent(props.percentage / 100)}
                    size="small"
                />
            </Box>
        </Box>
    )
}